import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from '../Paths/Api';

const SuperReport = () => {
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyTerm, setCompanyTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedMeeting, setExpandedMeeting] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Debounce search term
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch meetings and attendees
  const fetchMeetings = useCallback(async (purpose, company, start, end) => {
    setIsLoading(true);
    setError(null);
    try {
      // Validate dates
      if (start && end && new Date(start) > new Date(end)) {
        setError('Start date cannot be after end date');
        setIsLoading(false);
        return;
      }
      console.log('Fetching with:', { purpose, company, start_date: start, end_date: end });
      const response = await fetch(`${ApiURl}/get_superadmin_reports.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          purpose,
          company,
          start_date: start,
          end_date: end
        })
      });
      const data = await response.json();
      console.log('API Response:', data);
      if (data.success) {
        setMeetings(data.meetings);
      } else {
        setError(data.message || 'Failed to fetch meetings');
        setMeetings([]);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('An error occurred while fetching data');
      setMeetings([]);
    }
    setIsLoading(false);
  }, []);

  // Debounced fetch
  const debouncedFetch = useCallback(debounce(fetchMeetings, 500), [fetchMeetings]);

  // Trigger fetch on filter changes
  useEffect(() => {
    debouncedFetch(searchTerm, companyTerm, startDate, endDate);
  }, [searchTerm, companyTerm, startDate, endDate, debouncedFetch]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    navigate('/');
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Toggle meeting dropdown
  const toggleMeeting = (meetingId) => {
    setExpandedMeeting(expandedMeeting === meetingId ? null : meetingId);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setCompanyTerm('');
    setStartDate('');
    setEndDate('');
    setError(null);
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'S.No',
      'Meeting ID',
      'Purpose',
      'Location',
      'Time',
      'Description',
      'Status',
      'Created By',
      'Company',
      'GST Number',
      'Meeting Owner Company',
      'Meeting Owner GST',
      'Created At',
      'Attendee Name',
      'Attendee Email',
      'Attendee Phone',
      'Attendee Role',
      'Scan Status'
    ];
    const rows = [];
    meetings.forEach((meeting, index) => {
      if (meeting.attendees.length === 0) {
        rows.push([
          index + 1,
          meeting.id,
          meeting.purpose,
          meeting.location,
          meeting.time,
          meeting.description,
          meeting.status,
          meeting.created_by || 'N/A',
          meeting.company || 'N/A',
          meeting.gst_number || 'N/A',
          meeting.meeting_owner_company || 'N/A',
          meeting.meeting_owner_gst || 'N/A',
          meeting.created_at,
          '', '', '', '', ''
        ]);
      } else {
        meeting.attendees.forEach((attendee) => {
          rows.push([
            index + 1,
            meeting.id,
            meeting.purpose,
            meeting.location,
            meeting.time,
            meeting.description,
            meeting.status,
            meeting.created_by || 'N/A',
            meeting.company || 'N/A',
            meeting.gst_number || 'N/A',
            meeting.meeting_owner_company || 'N/A',
            meeting.meeting_owner_gst || 'N/A',
            meeting.created_at,
            attendee.name,
            attendee.email,
            attendee.phone,
            attendee.role || 'User',
            attendee.scan_status ? 'Scanned' : 'Not Scanned'
          ]);
        });
      }
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'superadmin_meetings_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate summary for analysis
  const summary = useMemo(() => {
    const totalMeetings = meetings.length;
    const totalAttendees = meetings.reduce((acc, meeting) => acc + meeting.attendees.length, 0);
    const scannedAttendees = meetings.reduce((acc, meeting) => acc + meeting.attendees.filter(a => a.scan_status).length, 0);
    const totalCompanies = [...new Set(meetings.map(m => m.company || 'N/A'))].length;
    const companiesWithGST = [...new Set(meetings.filter(m => m.gst_number && m.gst_number !== 'N/A').map(m => m.company))].length;
    return { totalMeetings, totalAttendees, scannedAttendees, totalCompanies, companiesWithGST };
  }, [meetings]);

  // Group meetings by company for divided display
  const groupedMeetings = useMemo(() => {
    return meetings.reduce((acc, meeting) => {
      const comp = meeting.company || 'N/A';
      if (!acc[comp]) acc[comp] = [];
      acc[comp].push(meeting);
      return acc;
    }, {});
  }, [meetings]);

  // Menu items
  const menuItems = [
    { name: 'Dashboard', path: '/scan', icon: 'M4 6h16M4 12h16M4 18h7' },
    { name: 'Reports', path: '/superReports', icon: 'M9 17v1m0 0H6a2 2 0 01-2-2V7a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2h-3m-6 0v1' }
  ];

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-[Inter]">
      {/* Embedded CSS */}
      <style jsx>{`
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .sidebar {
          transition: transform 0.3s ease-in-out;
        }
        @media (min-width: 1024px) {
          .sidebar {
            position: fixed;
            transform: translateX(0) !important;
          }
        }
        .table-container {
          overflow-x: auto;
        }
        .error-message {
          background-color: #fee2e2;
          color: #dc2626;
          padding: 1rem;
          border-radius: 0.5rem;
          margin-bottom: 1rem;
        }
      `}</style>

      {/* Mobile Sidebar Toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-teal-600 text-white rounded-lg shadow-md hover:bg-teal-700 transition-all duration-200 hover:shadow-lg"
        onClick={toggleSidebar}
        aria-label={isSidebarOpen ? 'Close Sidebar' : 'Open Sidebar'}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={isSidebarOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
          />
        </svg>
      </button>

      {/* Sidebar */}
      <div
        className={`fixed lg:static w-64 bg-gradient-to-b from-teal-600 to-cyan-600 text-white shadow-2xl transform transition-transform duration-300 ease-in-out sidebar ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 z-40 h-full lg:h-screen`}
      >
        <div className="p-6 border-b border-teal-700">
          <h2 className="text-xl font-bold flex items-center animate-slide-in">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Superadmin Dashboard
          </h2>
        </div>
        <ul className="mt-4 space-y-2 p-4">
          {menuItems.map((item, index) => (
            <li key={index}>
              <button
                onClick={() => {
                  navigate(item.path);
                  setIsSidebarOpen(false);
                }}
                className="flex items-center w-full text-left p-3 rounded-lg hover:bg-teal-700 hover:text-white transition-all duration-200 hover:shadow-md animate-slide-in"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
                {item.name}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Overlay for Mobile Sidebar */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto lg:ml-64">
        {/* Navbar */}
        <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg p-4 flex justify-between items-center fixed w-full lg:w-[calc(100%-16rem)] z-20">
          <h1 className="text-xl sm:text-2xl font-bold">Superadmin Reports</h1>
          <button
            onClick={handleLogout}
            className="flex items-center bg-teal-700 hover:bg-teal-800 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Logout
          </button>
        </nav>

        {/* Report Content */}
        <div className="p-6 sm:p-8 lg:p-10 bg-gray-50 min-h-[calc(100vh-4rem)] pt-20 animate-fade-in" style={{ marginTop: '70px' }}>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-800 mb-6 sm:mb-8 lg:mb-10">
            Meetings Report
          </h1>

          {/* Filters */}
          <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by meeting purpose..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 shadow-sm"
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by company..."
                value={companyTerm}
                onChange={(e) => setCompanyTerm(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 shadow-sm"
              />
            </div>
            <div className="flex gap-4 items-center">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 shadow-sm"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 shadow-sm"
              />
            </div>
            <div className="flex gap-4">
              <button
                onClick={exportToCSV}
                className="px-6 py-2.5 bg-teal-600 text-white rounded-lg shadow-md hover:bg-teal-700 transition-all duration-200 hover:shadow-lg"
              >
                Export to CSV
              </button>
              <button
                onClick={clearFilters}
                className="px-6 py-2.5 bg-gray-500 text-white rounded-lg shadow-md hover:bg-gray-600 transition-all duration-200 hover:shadow-lg"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Summary for Analysis */}
          <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-700">Total Meetings</h3>
              <p className="text-3xl font-bold text-teal-600">{summary.totalMeetings}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-700">Total Attendees</h3>
              <p className="text-3xl font-bold text-teal-600">{summary.totalAttendees}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-700">Scanned Attendees</h3>
              <p className="text-3xl font-bold text-teal-600">{summary.scannedAttendees}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-700">Total Companies</h3>
              <p className="text-3xl font-bold text-teal-600">{summary.totalCompanies}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-700">Companies with GST</h3>
              <p className="text-3xl font-bold text-teal-600">{summary.companiesWithGST}</p>
            </div>
          </div>

          {/* Meetings grouped by Company */}
          {Object.entries(groupedMeetings).map(([company, companyMeetings], groupIndex) => (
            <div key={groupIndex} className="mb-10">
              <h2 className="text-xl sm:text-2xl font-bold text-teal-700 mb-4">
                Company: {company}
              </h2>
              <div className="overflow-x-auto table-container">
                <table className="min-w-full bg-white rounded-lg shadow-md overflow-hidden">
                  <thead className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
                    <tr>
                      <th className="py-3 px-4 text-left">S.No</th>
                      <th className="py-3 px-4 text-left">Meeting ID</th>
                      <th className="py-3 px-4 text-left">Purpose</th>
                      <th className="py-3 px-4 text-left">Location</th>
                      <th className="py-3 px-4 text-left">Time</th>
                      <th className="py-3 px-4 text-left">Description</th>
                      <th className="py-3 px-4 text-left">Status</th>
                      {/* <th className="py-3 px-4 text-left">Created By</th>
                      <th className="py-3 px-4 text-left">Company</th>
                      <th className="py-3 px-4 text-left">GST Number</th> */}
                      <th className="py-3 px-4 text-left">Meeting Owner Company</th>
                      <th className="py-3 px-4 text-left">Meeting Owner GST</th>
                      <th className="py-3 px-4 text-left">Created At</th>
                      <th className="py-3 px-4 text-left">Attendees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyMeetings.map((meeting, index) => (
                      <React.Fragment key={meeting.id}>
                        <tr
                          onClick={() => toggleMeeting(meeting.id)}
                          className="cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <td className="py-3 px-4">{index + 1}</td>
                          <td className="py-3 px-4">{meeting.id}</td>
                          <td className="py-3 px-4">{meeting.purpose}</td>
                          <td className="py-3 px-4">{meeting.location}</td>
                          <td className="py-3 px-4">{new Date(meeting.time).toLocaleString()}</td>
                          <td className="py-3 px-4">{meeting.description}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                meeting.status === 'completed'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}
                            >
                              {meeting.status}
                            </span>
                          </td>
                          {/* <td className="py-3 px-4">{meeting.created_by || 'N/A'}</td>
                          <td className="py-3 px-4">{meeting.company || 'N/A'}</td>
                          <td className="py-3 px-4">{meeting.gst_number || 'N/A'}</td> */}
                          <td className="py-3 px-4">{meeting.meeting_owner_company || 'N/A'}</td>
                          <td className="py-3 px-4">{meeting.meeting_owner_gst || 'N/A'}</td>
                          <td className="py-3 px-4">{new Date(meeting.created_at).toLocaleString()}</td>
                          <td className="py-3 px-4">
                            <button
                              className="px-3 py-1 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                              onClick={() => toggleMeeting(meeting.id)}
                            >
                              {expandedMeeting === meeting.id ? 'Hide' : 'View'}
                            </button>
                          </td>
                        </tr>
                        {expandedMeeting === meeting.id && meeting.attendees.length > 0 && (
                          <tr>
                            <td colSpan="14" className="bg-gray-50 px-4 py-3">
                              <div className="overflow-x-auto">
                                <table className="min-w-full border border-gray-200">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="py-2 px-3 text-left">Name</th>
                                      <th className="py-2 px-3 text-left">Email</th>
                                      <th className="py-2 px-3 text-left">Phone</th>
                                      <th className="py-2 px-3 text-left">Role</th>
                                      <th className="py-2 px-3 text-left">Scan Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {meeting.attendees.map((attendee, idx) => (
                                      <tr key={idx} className="hover:bg-gray-50">
                                        <td className="py-2 px-3">{attendee.name}</td>
                                        <td className="py-2 px-3">{attendee.email}</td>
                                        <td className="py-2 px-3">{attendee.phone}</td>
                                        <td className="py-2 px-3">{attendee.role || 'User'}</td>
                                        <td className="py-2 px-3">
                                          {attendee.scan_status ? (
                                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                                              Scanned
                                            </span>
                                          ) : (
                                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                                              Not Scanned
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-8 text-teal-600 font-medium">Loading meetings...</div>
          )}

          {/* No Meetings Found */}
          {!isLoading && meetings.length === 0 && !error && (
            <div className="text-center py-8 text-gray-500 font-medium">No meetings found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperReport;
